import { Building, Building2, Check, Home, Warehouse } from "lucide-react";
import { Badge } from "@shared-kernel/primary/shared/shadcn/ui/badge";
import { STUDIO_OPTION } from "../../form-validation/shared";

export interface StudioOptionCardProps {
  title: string;
  description: string;
  isSelected: boolean;
  paymentBadgeText?: string;
  paymentBadgeVariant?: "success" | "warning";
  studioType: STUDIO_OPTION;
  additionalBadges?: Array<{
    text: string;
    variant?: "outline" | "secondary";
  }>;
  onClick: () => void;
  children?: React.ReactNode;
}

const getStudioIcon = (studioType: STUDIO_OPTION) => {
  if (studioType === STUDIO_OPTION.PARTNER) return Warehouse;
  if (studioType === STUDIO_OPTION.PERSONAL) return Home;
  if (studioType === STUDIO_OPTION.OTHER) return Building;
  return Building2;
};

export const StudioOptionCard = ({
  title,
  description,
  isSelected,
  paymentBadgeText,
  paymentBadgeVariant,
  additionalBadges = [],
  onClick,
  children,
  studioType,
}: StudioOptionCardProps) => {
  const Icon = getStudioIcon(studioType);
  const displayPaymentBadge = paymentBadgeText || additionalBadges.length > 0;
  return (
    <div
      className={`cursor-pointer rounded-xl border-2 p-4 transition-all hover:shadow-lg sm:p-6 ${
        isSelected ? "border-blue-500 bg-blue-50 shadow-md dark:bg-blue-950/50" : "border-border hover:border-muted-foreground"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start space-x-3 sm:space-x-4">
        <div className="shrink-0">
          <div className="flex size-10 items-center justify-center rounded-lg bg-blue-100 sm:size-12">
            <Icon className="size-5 text-blue-600 sm:size-6" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold sm:text-lg">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          {displayPaymentBadge && (
            <div className="mt-3 flex flex-col gap-2">
              {paymentBadgeText && (
                <Badge
                  variant="secondary"
                  className={`w-fit ${paymentBadgeVariant === "success" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}
                >
                  {paymentBadgeText}
                </Badge>
              )}
              {additionalBadges.map((badge, index) => (
                <Badge key={index} variant={badge.variant || "secondary"} className="w-fit">
                  {badge.text}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {isSelected && <Check className="size-5 text-blue-600" />}
      </div>

      {isSelected && children && <div className="mt-4 space-y-4 border-t border-blue-200 pt-4 sm:mt-6">{children}</div>}
    </div>
  );
};
